/**
 * Forgot Password Page Component
 * Request password reset via email
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material';
import { authService } from '../../services/authService';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('E-mail é obrigatório');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('E-mail inválido');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateEmail()) {
      return;
    }

    setLoading(true);

    try {
      await authService.forgotPassword({ email });
      setSuccess(true);
    } catch (err: any) {
      // Always show success message for security reasons
      // Even if email doesn't exist, we don't want to reveal that
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (emailError) {
      setEmailError(null);
    }
    if (error) {
      setError(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 'sm', mx: 'auto', px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
          }}
        >
          {success ? (
            // Success state
            (<Box sx={{ textAlign: 'center' }}>
              <CheckCircle
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h5" gutterBottom fontWeight={600}>
                E-mail Enviado!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Se o e-mail <strong>{email}</strong> estiver cadastrado no sistema, você receberá
                um link para redefinir sua senha.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Verifique também sua caixa de spam.
              </Typography>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                startIcon={<ArrowBack />}
                fullWidth
              >
                Voltar ao Login
              </Button>
            </Box>)
          ) : (
            // Form state
            (<>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
                  Esqueci minha Senha
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Digite seu e-mail para receber um link de recuperação de senha
                </Typography>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  label="E-mail"
                  value={email}
                  onChange={handleEmailChange}
                  error={!!emailError}
                  helperText={emailError}
                  disabled={loading}
                  autoComplete="email"
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Enviar Link de Recuperação'
                  )}
                </Button>

                <Button
                  component={Link}
                  to="/login"
                  fullWidth
                  variant="text"
                  startIcon={<ArrowBack />}
                  disabled={loading}
                >
                  Voltar ao Login
                </Button>
              </Box>
            </>)
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
